import React, { useMemo, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import {
  createLayer,
  deleteLayerIfEmpty,
  DEFAULT_LAYER_ID,
  setActiveLayer,
  toggleLayerLock,
  updateElement,
  selectTrackNode,
} from '../../features/layout/layoutSlice';
import { setBottomPanelOpen, setRightSidebarOpen } from '../../features/ui/uiSlice';
import { clearSelection, selectElement, selectElements } from '../../features/selection/selectionSlice';
import {
  IconLock,
  IconTrash,
  IconUnlock,
  VisibilityIcon,
} from '../icons/ContextMenuIcons';
import { deleteSelectedLayoutElements } from '../../utils/deleteCanvasSelection';

type DeleteConfirmState =
  | { open: false }
  | { open: true; targetType: 'item' | 'layer'; id: string; label: string };

const LayersPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const elements = useAppSelector((state) => state.layout.elements);
  const layers = useAppSelector((state) => state.layout.layers);
  const layerOrder = useAppSelector((state) => state.layout.layerOrder);
  const activeLayerId = useAppSelector((state) => state.layout.activeLayerId);
  const selectedIds = useAppSelector((state) => state.selection.selectedIds);
  const [showCreateLayerModal, setShowCreateLayerModal] = useState(false);
  const [newLayerName, setNewLayerName] = useState('');
  const [newLayerVisible, setNewLayerVisible] = useState(true);
  const [newLayerLocked, setNewLayerLocked] = useState(false);
  const [newLayerSetActive, setNewLayerSetActive] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({ open: false });

  const orderedLayers = useMemo(
    () => layerOrder.map((id) => layers[id]).filter(Boolean),
    [layerOrder, layers]
  );
  const activeLayer = orderedLayers.find((layer) => layer.id === activeLayerId) || null;
  const activeLayerElements = activeLayer
    ? activeLayer.elementIds.map((id) => elements[id]).filter(Boolean)
    : [];
  const canDeleteActiveLayer =
    !!activeLayer && activeLayer.id !== DEFAULT_LAYER_ID && activeLayer.elementIds.length === 0;

  const handleElementClick = (elementId: string) => {
    dispatch(selectTrackNode(null));
    dispatch(selectElement(elementId));
    dispatch(setBottomPanelOpen(false));
    dispatch(setRightSidebarOpen(true));
  };

  const handleLayerChange = (layerId: string) => {
    dispatch(setActiveLayer(layerId));

    const layerElementIds = layers[layerId]?.elementIds ?? [];
    if (layerElementIds.length > 0) {
      dispatch(selectElements(layerElementIds));
    } else {
      dispatch(clearSelection());
    }
  };

  const handleCreateLayer = () => {
    setNewLayerName('');
    setNewLayerVisible(true);
    setNewLayerLocked(false);
    setNewLayerSetActive(true);
    setShowCreateLayerModal(true);
  };

  const handleCreateLayerSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(
      createLayer({
        name: newLayerName.trim() || undefined,
        visible: newLayerVisible,
        locked: newLayerLocked,
        setActive: newLayerSetActive,
      })
    );
    setShowCreateLayerModal(false);
    setNewLayerName('');
    setNewLayerVisible(true);
    setNewLayerLocked(false);
    setNewLayerSetActive(true);
  };

  const handleCreateLayerCancel = () => {
    setShowCreateLayerModal(false);
    setNewLayerName('');
    setNewLayerVisible(true);
    setNewLayerLocked(false);
    setNewLayerSetActive(true);
  };

  const handleDeleteSingle = (elementId: string) => {
    const element = elements[elementId];
    const label = element?.metadata?.name || 'this item';
    setDeleteConfirm({
      open: true,
      targetType: 'item',
      id: elementId,
      label,
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirm.open) return;

    if (deleteConfirm.targetType === 'item') {
      deleteSelectedLayoutElements(dispatch, [deleteConfirm.id], elements);
    } else {
      dispatch(deleteLayerIfEmpty(deleteConfirm.id));
    }

    setDeleteConfirm({ open: false });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ open: false });
  };

  const handleActiveLayerLockToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!activeLayer) return;
    if (e.target.checked !== activeLayer.locked) {
      dispatch(toggleLayerLock(activeLayer.id));
    }
  };

  const handleDeleteActiveLayer = () => {
    if (!activeLayer || !canDeleteActiveLayer) return;
    setDeleteConfirm({
      open: true,
      targetType: 'layer',
      id: activeLayer.id,
      label: activeLayer.name,
    });
  };

  const handleVisibilityToggle = (elementId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const element = elements[elementId];
    dispatch(
      updateElement({
        id: elementId,
        updates: {
          metadata: {
            ...element.metadata,
            visible: !element.metadata.visible,
          },
        },
      })
    );
  };

  const handleLockToggle = (elementId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const element = elements[elementId];
    dispatch(
      updateElement({
        id: elementId,
        updates: {
          metadata: {
            ...element.metadata,
            locked: !element.metadata.locked,
          },
        },
      })
    );
  };

  return (
    <div className="h-full flex flex-col relative">
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Layers
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => dispatch(setBottomPanelOpen(false))}
              className="px-2 py-1 text-xs rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
              title="Close"
            >
              X
            </button>
          </div>
        </div>
        <div className="mt-3">
          {showCreateLayerModal && (
            <form
              onSubmit={handleCreateLayerSubmit}
              className="mb-3 rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 p-3 space-y-3"
            >
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Layer Name
                </label>
                <input
                  value={newLayerName}
                  onChange={(e) => setNewLayerName(e.target.value)}
                  placeholder="Enter layer name"
                  className="input-field w-full text-sm"
                  autoFocus
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={newLayerVisible}
                  onChange={(e) => setNewLayerVisible(e.target.checked)}
                />
                Visible
              </label>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={newLayerLocked}
                  onChange={(e) => setNewLayerLocked(e.target.checked)}
                />
                Locked
              </label>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={newLayerSetActive}
                  onChange={(e) => setNewLayerSetActive(e.target.checked)}
                />
                Set as active layer
              </label>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleCreateLayerCancel}
                  className="px-3 py-1.5 text-xs rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  Create Layer
                </button>
              </div>
            </form>
          )}

          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Layers
          </label>
          <select
            value={activeLayerId}
            onChange={(e) => handleLayerChange(e.target.value)}
            className="input-field w-full text-sm"
            title="Select active layer"
          >
            {orderedLayers.map((layer) => (
              <option key={layer.id} value={layer.id}>
                {layer.name} ({layer.elementIds.length})
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button
              type="button"
              onClick={handleCreateLayer}
              disabled={showCreateLayerModal}
              className="w-full px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Create a new layer"
            >
              Create Layer
            </button>

            <button
              type="button"
              onClick={handleDeleteActiveLayer}
              disabled={!canDeleteActiveLayer}
              className="w-full px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                activeLayer?.id === DEFAULT_LAYER_ID
                  ? 'Layer 1 cannot be deleted'
                  : activeLayer && activeLayer.elementIds.length > 0
                    ? 'Only empty layers can be deleted'
                    : 'Delete active layer'
              }
            >
              Delete Layer
            </button>
          </div>
          <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
            Active Layer Options
          </div>
          <div className="grid grid-cols-1 gap-2 items-center">
            <label className="w-full flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={!!activeLayer?.locked}
                onChange={handleActiveLayerLockToggle}
                disabled={!activeLayer}
              />
              Locked
            </label>
          </div>

          {deleteConfirm.open &&
            deleteConfirm.targetType === 'layer' &&
            activeLayer &&
            deleteConfirm.id === activeLayer.id && (
              <div className="mt-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-2">
                <div className="text-xs text-red-700 dark:text-red-300">
                  Delete layer &quot;{deleteConfirm.label}&quot;?
                </div>
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleDeleteCancel}
                    className="px-2 py-1 text-xs rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteConfirm}
                    className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {orderedLayers.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
            No layers
          </div>
        ) : !activeLayer ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
            Select a layer
          </div>
        ) : (
          <div className="p-2 space-y-1">
            <div className="px-2 pb-1 text-xs text-gray-500 dark:text-gray-400">
              {activeLayer.name}
            </div>
            {activeLayerElements.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                Empty layer
              </div>
            ) : (
              <div className="p-1 space-y-1">
                {activeLayerElements.map((element) => (
                  <React.Fragment key={element.id}>
                    <div
                      onClick={() => handleElementClick(element.id)}
                      className={`
                        group flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer relative
                        transition-all duration-150
                        ${selectedIds.includes(element.id)
                          ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-300 shadow-sm'
                          : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:shadow-sm'
                        }
                      `}
                    >
                      {/* Text and metadata */}
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {element.metadata.name}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                          <span className="truncate">{element.type}</span>
                          {element.metadata.locked && (
                            <span className="rounded bg-amber-50 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700">
                              Locked
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Property color and actions */}
                      <div className="flex shrink-0 items-center gap-0.5 border-l border-gray-200 pl-2 dark:border-gray-700">
                        <span
                          className="mr-1 h-4 w-4 shrink-0 rounded-full border border-black/15 shadow-sm"
                          style={{ backgroundColor: element.metadata.color || '#cccccc' }}
                          title={`Color: ${element.metadata.color || '#cccccc'}`}
                          aria-label={`Color ${element.metadata.color || '#cccccc'}`}
                        />
                        <button
                          onClick={(e) => handleVisibilityToggle(element.id, e)}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-700"
                          title={element.metadata.visible ? 'Hide' : 'Show'}
                        >
                          <VisibilityIcon visible={element.metadata.visible} className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleLockToggle(element.id, e)}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-700"
                          title={element.metadata.locked ? 'Unlock' : 'Lock'}
                        >
                          {element.metadata.locked ? (
                            <IconLock className="h-4 w-4" />
                          ) : (
                            <IconUnlock className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSingle(element.id);
                          }}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                          title="Delete item"
                        >
                          <IconTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {deleteConfirm.open &&
                      deleteConfirm.targetType === 'item' &&
                      deleteConfirm.id === element.id && (
                        <div
                          className="mx-2 mt-1 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="text-xs text-red-700 dark:text-red-300">
                            Delete {deleteConfirm.label}?
                          </div>
                          <div className="mt-2 flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={handleDeleteCancel}
                              className="px-2 py-1 text-xs rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleDeleteConfirm}
                              className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LayersPanel;
